import React, { useState, useEffect } from 'react';
import { collection, updateDoc, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import { FaTimes, FaSearch, FaUserShield, FaCheckCircle } from 'react-icons/fa';

const ManageAccessModal = ({ isOpen, onClose, appName, appId }) => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployees, setSelectedEmployees] = useState(new Set());

    useEffect(() => {
        let unsubscribe;
        if (isOpen) {
            setLoading(true);
            const employeesRef = collection(db, "employees");

            unsubscribe = onSnapshot(employeesRef, (snapshot) => {
                const emps = [];
                const authorizedIds = new Set();

                snapshot.docs.forEach((doc) => {
                    /*  */const data = doc.data();
                    emps.push({ id: doc.id, ...data });

                    // Check if user already has access
                    if (data.techAccess && data.techAccess.includes(appId)) {
                        authorizedIds.add(doc.id);
                    }
                });

                setEmployees(emps);
                setSelectedEmployees(authorizedIds);
                setLoading(false);
            }, (error) => {
                console.error("Error fetching employees:", error);
                setLoading(false);
            });
        }
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [isOpen, appId]);

    const toggleAccess = async (employeeId) => {
        // Determine intent from current render cycle state (safe for distinct items)
        const isSelected = selectedEmployees.has(employeeId);
        const willSelect = !isSelected;

        try {
            // Optimistic UI Update using Functional State (Fixes the race condition)
            setSelectedEmployees(prev => {
                const next = new Set(prev);
                if (willSelect) next.add(employeeId);
                else next.delete(employeeId);
                return next;
            });

            // DB Update
            const empRef = doc(db, "employees", employeeId);
            const empSnap = await getDoc(empRef);

            if (empSnap.exists()) {
                const empData = empSnap.data();
                const currentTechAccess = empData.techAccess || [];
                let newAccessList;

                if (willSelect) {
                    // Add if not present
                    newAccessList = currentTechAccess.includes(appId)
                        ? currentTechAccess
                        : [...currentTechAccess, appId];
                } else {
                    // Remove
                    newAccessList = currentTechAccess.filter(id => id !== appId);
                }

                await updateDoc(empRef, {
                    techAccess: newAccessList
                });
            }

        } catch (error) {
            console.error("Error updating access:", error);
            // Revert on error (fetch real state)
            console.warn("Failed to update access, please refresh.");
        }
    };

    const filteredEmployees = employees.filter(emp =>
        emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-neutral-900 border border-neutral-700 w-full max-w-lg rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">

                {/* Header */}
                <div className="p-6 border-b border-neutral-800 flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <FaUserShield className="text-blue-400" /> Manage Access
                        </h2>
                        <p className="text-gray-400 text-sm mt-1">
                            Select employees who can view the <span className="text-blue-300 font-semibold">{appName}</span> tracker.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition p-2 hover:bg-neutral-800 rounded-full">
                        <FaTimes className="text-xl" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-4 border-b border-neutral-800 bg-neutral-900/50">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            className="w-full bg-neutral-800 text-white rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 border border-neutral-700"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Employee List */}
                <div className="flex-1 overflow-y-auto p-2">
                    {loading ? (
                        <div className="flex justify-center items-center h-40 text-gray-500">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mr-3"></div>
                            Loading...
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filteredEmployees.map(emp => {
                                const isSelected = selectedEmployees.has(emp.id);
                                return (
                                    <div
                                        key={emp.id}
                                        className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${isSelected ? 'bg-blue-500/10 border border-blue-500/30' : 'hover:bg-neutral-800 border border-transparent'
                                            }`}
                                        onClick={() => toggleAccess(emp.id)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${isSelected ? 'bg-blue-500' : 'bg-neutral-700'
                                                }`}>
                                                {emp.name?.charAt(0) || 'U'}
                                            </div>
                                            <div>
                                                <div className={`font-semibold ${isSelected ? 'text-blue-200' : 'text-gray-200'}`}>
                                                    {emp.name}
                                                </div>
                                                <div className="text-xs text-gray-500">{emp.email} • {emp.role || 'Employee'}</div>
                                            </div>
                                        </div>

                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-neutral-600'
                                            }`}>
                                            {isSelected && <FaCheckCircle className="text-white text-xs" />}
                                        </div>
                                    </div>
                                );
                            })}
                            {filteredEmployees.length === 0 && (
                                <div className="text-center py-10 text-gray-500">No employees found.</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-neutral-800 bg-neutral-900/50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ManageAccessModal;
